import React, { useState, useEffect } from 'react';
import { Container, Typography, Paper, Box, TextField, Button, Alert, Grid, Divider, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { apiClient } from '../contexts/AuthContext';

interface UserInfo {
    id: number;
    username: string;
    creation_date: string;
    settings?: null | {
        pch_hours_prestataire: number;
        pch_hours_aidant: number;
        pch_hours_emploi_direct: number;
    };
}

interface McpTokenResponse {
    token: string;
    created_at: string;
}

const AccountSettings = () => {
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [passwordForm, setPasswordForm] = useState({
        current_password: '',
        new_password: '',
        confirm_password: ''
    });
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [pchForm, setPchForm] = useState({
        pch_hours_prestataire: '',
        pch_hours_aidant: '',
        pch_hours_emploi_direct: '',
    });
    const [pchSaving, setPchSaving] = useState(false);
    const [mcpToken, setMcpToken] = useState<McpTokenResponse | null>(null);
    const [mcpLoading, setMcpLoading] = useState(true);
    const [mcpError, setMcpError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [regeneratingToken, setRegeneratingToken] = useState(false);
    const appOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    // Single-endpoint URL expected by MCP clients; /mcp/rpc still resolves
    // for anyone already configured against the older path.
    const mcpRpcUrl = appOrigin ? `${appOrigin}/mcp` : '/mcp';
    const mcpHealthUrl = appOrigin ? `${appOrigin}/mcp/health` : '/mcp/health';
    const mcpTokenUrl = appOrigin ? `${appOrigin}/mcp/oauth/token` : '/mcp/oauth/token';
    const mcpAuthorizeUrl = appOrigin ? `${appOrigin}/mcp/oauth/authorize` : '/mcp/oauth/authorize';
    const clientIdHint = userInfo?.username ?? 'votre_identifiant';

    useEffect(() => {
        fetchUserInfo();
        fetchMcpToken();
    }, []);

    const fetchUserInfo = async () => {
        try {
            const response = await apiClient.get<UserInfo>('/api/users/me');
            setUserInfo(response.data);
            // Fetch settings in case backend sends separate values or user has none
            try {
                const settingsResp = await apiClient.get('/api/users/settings');
                const s = settingsResp.data as { pch_hours_prestataire: number; pch_hours_aidant: number; pch_hours_emploi_direct: number };
                setPchForm({
                    pch_hours_prestataire: String(s.pch_hours_prestataire ?? ''),
                    pch_hours_aidant: String(s.pch_hours_aidant ?? ''),
                    pch_hours_emploi_direct: String(s.pch_hours_emploi_direct ?? ''),
                });
            } catch {}
            setLoading(false);
        } catch (err) {
            setError('Erreur lors du chargement des informations utilisateur');
            setLoading(false);
        }
    };

    const handleRegenerateToken = async () => {
        if (regeneratingToken) {
            return;
        }
        setRegeneratingToken(true);
        setMcpError(null);
        setSuccess(null);
        try {
            const response = await apiClient.post<McpTokenResponse>('/api/users/mcp-token/regenerate');
            setMcpToken(response.data);
            setCopied(false);
            setSuccess('Token MCP régénéré');
        } catch (err) {
            setMcpError('Erreur lors de la régénération du token MCP');
        } finally {
            setRegeneratingToken(false);
        }
    };

    const handleCopyToken = async () => {
        if (!mcpToken) {
            return;
        }
        try {
            await navigator.clipboard.writeText(mcpToken.token);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            setMcpError('Impossible de copier le token. Copiez-le manuellement.');
        }
    };

    const fetchMcpToken = async () => {
        setMcpLoading(true);
        setMcpError(null);
        try {
            const response = await apiClient.get<McpTokenResponse>('/api/users/mcp-token');
            setMcpToken(response.data);
        } catch (err) {
            setMcpError('Impossible de récupérer votre token MCP pour le moment.');
        } finally {
            setMcpLoading(false);
        }
    };
    const handlePchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        // Allow only numbers and dot/comma
        if (/^\d*(?:[.,]\d{0,2})?$/.test(value) || value === '') {
            setPchForm(prev => ({ ...prev, [name]: value.replace(',', '.') }));
        }
    };

    const handlePchSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSuccess(null);
        setError(null);
        setPchSaving(true);
        try {
            await apiClient.put('/api/users/settings', {
                pch_hours_prestataire: Number(pchForm.pch_hours_prestataire || 0),
                pch_hours_aidant: Number(pchForm.pch_hours_aidant || 0),
                pch_hours_emploi_direct: Number(pchForm.pch_hours_emploi_direct || 0),
            });
            setSuccess('Paramètres PCH enregistrés');
        } catch (e) {
            setError('Erreur lors de la sauvegarde des paramètres PCH');
        } finally {
            setPchSaving(false);
        }
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPasswordForm({
            ...passwordForm,
            [e.target.name]: e.target.value
        });
    };

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setPasswordError(null);
        setSuccess(null);

        if (passwordForm.new_password !== passwordForm.confirm_password) {
            setPasswordError('Les mots de passe ne correspondent pas');
            return;
        }

        try {
            await apiClient.post('/api/users/change-password', {
                current_password: passwordForm.current_password,
                new_password: passwordForm.new_password
            });
            setSuccess('Mot de passe modifié avec succès');
            setPasswordForm({
                current_password: '',
                new_password: '',
                confirm_password: ''
            });
        } catch (err) {
            setPasswordError('Erreur lors du changement de mot de passe');
        }
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Typography>Chargement...</Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
            <Paper sx={{ p: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ fontFamily: 'Poppins, Inter, sans-serif', fontWeight: 700 }}>
                    Mon compte
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                            Informations du compte
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body1">
                                <strong>Nom d'utilisateur:</strong> {userInfo?.username}
                            </Typography>
                            <Typography variant="body1">
                                <strong>Date de création:</strong> {new Date(userInfo?.creation_date || '').toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                            Connexion AuxTrack MCP
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Utilisez ce token pour connecter le serveur MCP <strong>AuxTrack</strong> à ChatGPT ou à un autre client compatible. Le token authentifie l&apos;accès à vos plannings et statistiques.
                        </Typography>
                        {mcpError && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {mcpError}
                            </Alert>
                        )}
                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                <TextField
                                    label="Token MCP"
                                    value={mcpToken?.token ?? ''}
                                    InputProps={{ readOnly: true }}
                                    sx={{ minWidth: 280, flexGrow: 1 }}
                                    helperText={
                                        mcpLoading
                                            ? 'Chargement...'
                                            : mcpToken
                                                ? 'Copiez ce token dans la configuration de votre client MCP'
                                                : 'Générez un token MCP pour démarrer'
                                    }
                                />
                                <Tooltip title={copied ? 'Copié !' : 'Copier'}>
                                    <span>
                                        <IconButton color={copied ? 'success' : 'primary'} onClick={handleCopyToken} disabled={!mcpToken || mcpLoading}>
                                            <ContentCopyIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleRegenerateToken}
                                    disabled={mcpLoading || regeneratingToken}
                                >
                                    {regeneratingToken ? 'Régénération...' : 'Régénérer le token'}
                                </Button>
                                {mcpToken?.created_at && (
                                    <Typography variant="caption" color="text.secondary">
                                        Dernière génération : {new Date(mcpToken.created_at).toLocaleString('fr-FR')}
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Guide de connexion ChatGPT
                                </Typography>
                                <Box component="ol" sx={{ pl: 3, mt: 0.5, color: 'text.secondary' }}>
                                    <li>
                                        Dans ChatGPT, ouvrez <strong>Settings &gt; Model Context Protocol &gt; Add HTTP server</strong>.
                                    </li>
                                    <li>
                                        Renseignez l&apos;URL du serveur&nbsp;: <code>{mcpRpcUrl}</code> et l&apos;URL de santé&nbsp;: <code>{mcpHealthUrl}</code>.
                                    </li>
                                    <li>
                                        Sélectionnez <strong>OAuth 2.0</strong> puis le flux <strong>Client Credentials</strong>. Utilisez&nbsp;: Authorization URL <code>{mcpAuthorizeUrl}</code>, Token URL <code>{mcpTokenUrl}</code>, Client ID = <code>{clientIdHint}</code>, Client Secret = le token MCP ci-dessus.
                                    </li>
                                    <li>
                                        Validez. ChatGPT récupère automatiquement un jeton d&apos;accès et transmettra l&apos;entête <code>Authorization: Bearer ...</code> lors de chaque appel.
                                    </li>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    Alternative rapide&nbsp;: choisissez l&apos;authentification «&nbsp;API key&nbsp;» et fournissez <code>Authorization</code> = <code>Bearer &lt;token MCP&gt;</code> si vous préférez éviter OAuth.
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                            Heures mensuelles PCH
                        </Typography>
                        <Box component="form" onSubmit={handlePchSubmit} sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Forfait mensuel Prestataire"
                                        name="pch_hours_prestataire"
                                        value={pchForm.pch_hours_prestataire}
                                        onChange={handlePchChange}
                                        inputMode="decimal"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Forfait mensuel Aidant"
                                        name="pch_hours_aidant"
                                        value={pchForm.pch_hours_aidant}
                                        onChange={handlePchChange}
                                        inputMode="decimal"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Forfait mensuel Emploi direct"
                                        name="pch_hours_emploi_direct"
                                        value={pchForm.pch_hours_emploi_direct}
                                        onChange={handlePchChange}
                                        inputMode="decimal"
                                    />
                                </Grid>
                            </Grid>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                sx={{ mt: 2 }}
                                disabled={pchSaving}
                            >
                                {pchSaving ? 'Enregistrement...' : 'Enregistrer'}
                            </Button>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
                            Changer le mot de passe
                        </Typography>
                        <Box component="form" onSubmit={handlePasswordSubmit} sx={{ mt: 2 }}>
                            {passwordError && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {passwordError}
                                </Alert>
                            )}
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Mot de passe actuel"
                                type="password"
                                name="current_password"
                                value={passwordForm.current_password}
                                onChange={handlePasswordChange}
                                required
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Nouveau mot de passe"
                                type="password"
                                name="new_password"
                                value={passwordForm.new_password}
                                onChange={handlePasswordChange}
                                required
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Confirmer le nouveau mot de passe"
                                type="password"
                                name="confirm_password"
                                value={passwordForm.confirm_password}
                                onChange={handlePasswordChange}
                                required
                            />
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                sx={{ mt: 2 }}
                            >
                                Changer le mot de passe
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
        </Container>
    );
};

export default AccountSettings; 
